import { useCallback, useMemo, useRef } from 'react';
import { Link, LinkProps, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { getLearnPath, learnPageFromPath } from '../../../../common/src/pages.ts';
import { learnClassBackLabel, learnClassContinueLabel, learnClasses, learnModuleStartLabel } from './learn_classes.ts';

// How the Learn tab's screens are entered and left (follow-up to issue #3719), in one place.
//
// Entering. Every in-app way into a Learn sub-page - a hub card, a class screen's tile, the Range
// Finder's call to action, the hub's part line, the profile drawer's "My vocal range" - carries
// React Router location state naming the screen the singer was on: `{ learnFrom: <its pathname> }`.
// The invariant the rest relies on: when an entry carries `learnFrom`, the history entry before it
// is that path. Router state lives with the history entry, so it survives a reload, and it is
// absent on a direct URL or a new tab - which is exactly the case where the origin is unknown.
//
// Leaving. An exit never pushes. It goes back one entry when the entry before is the place the
// exit is meant to reach (so the finished screen is not left in the history behind the singer for
// Android's hardware back button to walk through again), and otherwise replaces the current entry
// with its target. A replacing exit, like a forward hop that must not leave a stale entry behind
// (the Range Finder's result leading on, Pitch Match's redirect into the Range Finder), hands on
// the same `learnFrom`: the entry it replaces had that predecessor, and so does its replacement.
//
// Going on (issue #3721). An entry into the Range Finder made for one module - a class screen's tile
// for a module that needs a range, or a module page's own redirect when no range is saved - also
// carries `{ learnNext: <the module's path> }`, so the Range Finder's result leads on into the
// module the singer came for rather than the class's first ("Start Interval Training", not "Start
// Pitch Match"). It is read as defensively as `learnFrom`, and only a path of a module that needs a
// range counts. A replacing hop hands it on with `learnFrom`; the hop into the module uses it up.
// Without it (the hub's card, the profile drawer, a direct link to the Range Finder) the way on is
// worked out from `learnFrom` alone.
//
// The decisions are pure functions (learnExit, learnOriginExit, learnHop, learnOriginLabel,
// learnRangeResultNext) so they can be tested branch by branch; the hooks and LearnLink only read
// the location and follow them.

export interface LearnLocationState {
  learnFrom?: string; // the pathname of the screen the singer came from: the history entry before
  learnNext?: string; // the path of the module an entry into the Range Finder was made for
}

export const backToLearnLabel = 'Back to Learn';
export const plainBackLabel = 'Back';

// The state an in-app navigation into a Learn sub-page carries, made on the screen it leaves, with
// the module it is made for when it leads into the Range Finder on that module's behalf
export function learnEntryState(fromPathname: string, next?: string): LearnLocationState {
  return next === undefined ? { learnFrom: fromPathname } : { learnFrom: fromPathname, learnNext: next };
}

// Where the singer came from, read defensively: location state is whatever the navigation that
// made the entry put there, so anything but an app path is no origin at all
export function learnFromState(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null) {
    return undefined;
  }
  const { learnFrom } = state as { learnFrom?: unknown };
  return typeof learnFrom === 'string' && learnFrom.startsWith('/') ? learnFrom : undefined;
}

// The module the singer is on their way to, read as defensively as learnFromState: the path of a
// module that needs a range, or nothing
export function learnNextFromState(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null) {
    return undefined;
  }
  const { learnNext } = state as { learnNext?: unknown };
  return typeof learnNext === 'string' && rangeModuleAt(learnNext) ? learnNext : undefined;
}

// What a way out or a forward hop does to the history: step back one entry, or replace the current
// one (never push)
export type LearnNavigation = { kind: 'back' } | { kind: 'replace'; to: string; state?: LearnLocationState };

// A trailing slash names the same screen ('/learn/' is the hub)
function samePath(a: string, b: string): boolean {
  const trim = (path: string) => path.replace(/\/+$/, '') || '/';
  return trim(a) === trim(b);
}

// The state an entry that takes the current one's place carries: the same origin, since the entry
// before is unchanged, so the invariant still holds for the replacement, and the module the singer
// is on their way to when there is one
export function learnHandedOnState(from: string | undefined, next?: string): LearnLocationState | undefined {
  if (from === undefined && next === undefined) {
    return undefined;
  }
  return { ...(from !== undefined && { learnFrom: from }), ...(next !== undefined && { learnNext: next }) };
}

// A hop that takes the current entry's place, handing on where the singer came from and where they
// are going
export function learnHop(to: string, from: string | undefined, next?: string): LearnNavigation {
  return { kind: 'replace', to, state: learnHandedOnState(from, next) };
}

// Leaving for a known place (a class screen for the hub, a module for its class screen): back when
// the entry before is that place, otherwise the current entry is replaced with it
export function learnExit(target: string, from: string | undefined): LearnNavigation {
  if (from !== undefined && samePath(from, target)) {
    return { kind: 'back' };
  }
  return learnHop(target, from);
}

// Leaving for wherever the singer came from (the Range Finder): back when that is known, the hub in
// the current entry's place when it is not (a direct link, a new tab)
export function learnOriginExit(from: string | undefined): LearnNavigation {
  return learnExit(from ?? getLearnPath(), from);
}

// What the way back to the origin says: "Back to Learn" for the hub and for an origin nobody knows
// (which leads to the hub), "Back to Pitch" for a class screen, plain "Back" for anywhere else
// (Today, Church... through the profile drawer)
export function learnOriginLabel(from: string | undefined): string {
  if (from === undefined || samePath(from, getLearnPath())) {
    return backToLearnLabel;
  }
  const page = learnPageFromPath(from);
  const learnClass = page && learnClasses.find((info) => info.screen?.page === page);
  return learnClass ? learnClassBackLabel(learnClass) : plainBackLabel;
}

// Where the Range Finder's result leads on to, and what its primary button says. A singer sent for a
// module (`next`: a module's tile, or its own redirect) came for that module, so "Start Interval
// Training" opens it. Otherwise the Range Finder is for the class whose module needs a range (Pitch,
// for Pitch Match): a singer who came from that class screen - its find-your-range pill - came for
// its first such module, so "Start Pitch Match" opens it, and from anywhere else (the hub, the
// profile drawer on any tab, an origin nobody knows) the result leads to the class screen, which
// lists the modules: "Continue to Pitch". Either way the Range Finder's entry is replaced and its
// origin handed on, so from the hub the history is the hub and then the class screen, whose arrow
// steps back to it.
export interface LearnRangeResultNext {
  label: string;
  navigation: LearnNavigation;
}

export function learnRangeResultNext(from: string | undefined, next?: string): LearnRangeResultNext {
  const wanted = next === undefined ? undefined : rangeModuleAt(next);
  if (wanted) {
    return { label: learnModuleStartLabel(wanted), navigation: learnHop(getLearnPath(wanted.built.page), from) };
  }
  const { learnClass, module } = rangeClassAndModule();
  const classPath = getLearnPath(learnClass.screen.page);
  if (from !== undefined && samePath(from, classPath)) {
    return { label: learnModuleStartLabel(module), navigation: learnHop(getLearnPath(module.built.page), from) };
  }
  return { label: learnClassContinueLabel(learnClass), navigation: learnHop(classPath, from) };
}

// The module at `path` when it is one a Range Finder entry can be made for: built, needing a range,
// in a class with a screen
function rangeModuleAt(path: string) {
  for (const learnClass of learnClasses) {
    const module = learnClass.modules.find(
      (info) => info.built?.needsRange && samePath(getLearnPath(info.built.page), path),
    );
    if (module?.built && learnClass.screen) {
      return { ...module, built: module.built };
    }
  }
  return undefined;
}

// The class with a screen whose module needs a range, and that module: the first in the list
function rangeClassAndModule() {
  for (const learnClass of learnClasses) {
    const module = learnClass.modules.find((info) => info.built?.needsRange);
    const { screen } = learnClass;
    if (module?.built && screen) {
      return { learnClass: { ...learnClass, screen }, module: { ...module, built: module.built } };
    }
  }
  throw new Error('No Learn class has a module that needs the Range Finder');
}

export async function followLearnNavigation(navigate: NavigateFunction, navigation: LearnNavigation) {
  if (navigation.kind === 'back') {
    await navigate(-1);
    return;
  }
  await navigate(navigation.to, { replace: true, state: navigation.state });
}

// Where the current entry says the singer came from
export function useLearnFrom(): string | undefined {
  return learnFromState(useLocation().state);
}

// The module the current entry says the singer is on their way to
export function useLearnNext(): string | undefined {
  return learnNextFromState(useLocation().state);
}

// Follows one navigation per visit to a screen. A way out that steps back is not idempotent: a
// second tap before the screen has gone (a slow phone tearing its audio down) would step back a
// second entry, past the place the singer meant to reach. Keyed by the history entry, so a screen
// that stays mounted across a navigation can leave again.
function useFollowOnce(): (navigation: LearnNavigation) => Promise<void> {
  const navigate = useNavigate();
  const { key } = useLocation();
  const followedForRef = useRef<string | undefined>(undefined);
  return useCallback(
    async (navigation: LearnNavigation) => {
      if (followedForRef.current === key) {
        return;
      }
      followedForRef.current = key;
      await followLearnNavigation(navigate, navigation);
    },
    [navigate, key],
  );
}

// The way out of a screen whose way out is a known place (the class screens, the modules)
export function useLearnExit(target: string): () => Promise<void> {
  const follow = useFollowOnce();
  const from = useLearnFrom();
  return useCallback(() => follow(learnExit(target, from)), [follow, target, from]);
}

// The Range Finder's two ways out, with what their buttons say: back to wherever the singer came
// from (see learnOriginExit), and on from its result (see learnRangeResultNext). One hook, so the
// two share one follow-once: a tap on one button and then the other before the screen has gone
// must not follow both (the way on replaces the entry, and a step back after it would leave the
// place it had just reached).
export function useLearnRangeFinderWaysOut(): {
  backLabel: string;
  onBack: () => Promise<void>;
  nextLabel: string;
  onNext: () => Promise<void>;
} {
  const follow = useFollowOnce();
  const from = useLearnFrom();
  const wanted = useLearnNext();
  // the page renders on every analysed frame: work the way on out once per origin, not per frame
  const next = useMemo(() => learnRangeResultNext(from, wanted), [from, wanted]);
  const onBack = useCallback(() => follow(learnOriginExit(from)), [follow, from]);
  const onNext = useCallback(() => follow(next.navigation), [follow, next]);
  return { backLabel: learnOriginLabel(from), onBack, nextLabel: next.label, onNext };
}

// A link into a Learn sub-page, carrying the screen it is tapped on as the way back, and `next`, the
// module it leads into the Range Finder for, when it does
export const LearnLink = ({ next, ...props }: Omit<LinkProps, 'state'> & { next?: string }) => {
  const { pathname } = useLocation();
  return <Link {...props} state={learnEntryState(pathname, next)} />;
};
